const AbstractPaymentMethodConfig = require('@/lib/PaymentMethods/AbstractPaymentMethodConfig');
const { splitCamelCase, toTitleCase } = require('@/functions/strings');
const { toBool, toInt, toNullableString, toString } = require('@/functions/transform');

class DojoConfig extends AbstractPaymentMethodConfig {
    constructor() {
        super();

        /**
         * @type {boolean}
         */
        this.allowCashBack = true;

        /**
         * @type {string|null}
         */
        this.currency = null;

        /**
         * @type {boolean}
         */
        this.isDojoWebPos = false;

        /**
         * This is a url in the format https://sv733l250000.test.connect.paymentsense.cloud/
         * This will usually be set at company level.
         *
         * @type {string}
         */
        this.terminalHost = null;

        /**
         * API key provided by Dojo.
         * This will usually be set at company level.
         *
         * @type {string}
         */
        this.apiKey = null;

        /**
         * Provided by Dojo. For our integration this should be the same value as installerId.
         * This value will never change and is the same across all environments.
         *
         * @type {string}
         */
        this.softwareHouseId = 'sv733l25';

        /**
         * Only Ingenico terminals support reports but there is no way to tell if it is an Ingenico terminal
         * via the API so we have to set this property manually in the Dojo Settings.
         *
         * @type {boolean}
         */
        this.supportsReports = false;

        /**
         * Provided by Dojo. For our integration this should be the same value as softwareHouseId.
         *
         * @type {string}
         */
        this.installerId = 'sv733l25';

        /**
         * Used as part of Basic Auth. This can be anything but we can use it to identify the device so use deviceID.
         * This property is set in DojoServices.js.
         *
         * @type {string}
         */
        this.username = null;

        /**
         * The unique id of the terminal. These can be fetched from the Dojo /pac/terminals/ endpoint or details for a
         * specific terminal can be fetched from /pac/terminals/{tid}.
         *
         * During development or testing we can use specific test terminalIds to
         * replicate different transaction results:
         * VCMDTSLSIP0 - simulates a successful chip and pin payment
         * VCMDTSLDIP0 - simulates a declined chip and pin payment
         * VCMDTSLSCD0 - simulates a contactless payment with device verification
         * VCMDTSLSIS0 - simulates a signature payment
         * VCMDTSLUIP0 - simulates an unsuccessful payment result
         * VCMDTSLTIP0 - simulates a "TIMED_OUT" payment result
         *
         * @type {string}
         */
        this.terminalId = null;

        /**
         * The version of the REST API we are using.
         *
         * @type {string}
         */
        this.apiAcceptVersion = 'application/connect.v2+json';

        /**
         * Polling period in milliseconds recommended by Dojo when a transaction is in progress.
         *
         * @type {number}
         */
        this.pollPeriod = 1000;
    }

    /**
     * Returns true if this is a valid configuration.
     * Otherwise, throws an error.
     *
     * @param {boolean} validateTerminalId
     * @param {boolean} validateNonUserSuppliedValues Skip validating values that do not need to be supplied in
     *                                                either the Dojo settings modal or at company level?
     *
     * @return {boolean}
     */
    validate(
        validateTerminalId = true,
        validateNonUserSuppliedValues = true
    ) {
        let required = [
            // We need to be able to validate the config before the terminalId has been set so that we can make
            // a request to choose a terminal.
            validateTerminalId ? 'terminalId' : null,
            'terminalHost',
            validateNonUserSuppliedValues ? 'username' : null, // We use device.ID as username.
            'installerId',
            'softwareHouseId',
            'apiKey',
        ].filter((v) => !!v);

        for (let i = 0; i < required.length; i++) {
            if (!this[required[i]]) {
                // Format the field nicely e.g. terminalId -> Terminal ID.
                const field = toTitleCase(splitCamelCase(required[i])).replace('Id', 'ID');
                throw new Error('The ' + field + ' value must be set.');
            }
        }

        if (!this.terminalHost.startsWith('https://')) {
            throw new Error('The Terminal Host field should be a URL beginning with https:// that contains your Account ID. This field should normally be configured in the Back Office.');
        }

        return true;
    }

    getHydrationMap() {
        return {
            allowCnp: toBool,
            allowCashBack: toBool,
            apiKey: toString,
            apiAcceptVersion: toString,
            currency: toNullableString,
            installerId: toString,
            isDojoWebPos: toBool,
            pollPeriod: toInt,
            softwareHouseId: toString,
            supportsReports: toBool,
            terminalId: toString,
            terminalHost: toString,
            username: toString,
        };
    }
}

module.exports = DojoConfig;
